import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Snackbar,
  Alert,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  MenuItem,
  Grid,
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import RefreshIcon from '@mui/icons-material/Refresh';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ReceiptIcon from '@mui/icons-material/Receipt';
import PaymentIcon from '@mui/icons-material/Payment';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import config from '../../../config';

const STATUS_COLOR = {
  Draft:     { bg: '#fef3c7', color: '#92400e' },
  Processed: { bg: '#dbeafe', color: '#1e40af' },
  Paid:      { bg: '#dcfce7', color: '#166534' },
  Locked:    { bg: '#f1f5f9', color: '#475569' },
};

// HR Payroll Processing — pick month/year, generate (single or bulk),
// adjust manual additions, lock/unlock the month. Status flow is
// Draft → Processed → Paid (payment posting lives in Phase 6).
export default function Payroll() {
  const token = sessionStorage.getItem('token');
  const company = JSON.parse(sessionStorage.getItem('companyDetails') || '{}');
  const user = JSON.parse(sessionStorage.getItem('user') || '{}');
  const companyId = company?.id;

  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [departmentFilter, setDepartmentFilter] = useState('');
  const [empFilter, setEmpFilter] = useState('');

  const [departments, setDepartments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  const [isLocked, setIsLocked] = useState(false);

  const [detailOpen, setDetailOpen] = useState(false);
  const [detail, setDetail] = useState(null);

  const [adjustOpen, setAdjustOpen] = useState(false);
  const [adjustForm, setAdjustForm] = useState({
    id: 0,
    incentive: 0,
    bonus: 0,
    otherEarnings: 0,
    otherDeductions: 0,
  });

  // Payment recording dialog
  const [payOpen, setPayOpen] = useState(false);
  const [payTarget, setPayTarget] = useState(null);
  const [payForm, setPayForm] = useState({
    paymentDate: new Date().toISOString().slice(0, 10),
    amount: 0,
    payMode: 'Cash',
    referenceNo: '',
    bankName: '',
    remarks: '',
  });

  const [snack, setSnack] = useState({ open: false, msg: '', sev: 'success' });
  const toast = (m, s = 'success') => setSnack({ open: true, msg: m, sev: s });

  const loadRefs = async () => {
    const [eRes, dRes] = await Promise.all([
      fetch(`${config.API_BASE_URL}/Hr/Employees/${companyId}`, {
        headers: { Authorization: `Bearer ${token}` },
      }),
      fetch(`${config.API_BASE_URL}/Hr/Departments/${companyId}`, {
        headers: { Authorization: `Bearer ${token}` },
      }),
    ]);
    if (eRes.ok) setEmployees(await eRes.json());
    if (dRes.ok) setDepartments(await dRes.json());
  };

  const load = async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      qs.append('month', month);
      qs.append('year', year);
      if (empFilter) qs.append('employeeId', empFilter);
      if (departmentFilter) qs.append('departmentId', departmentFilter);
      const [pRes, lRes] = await Promise.all([
        fetch(
          `${config.API_BASE_URL}/Hr/Payroll/${companyId}?${qs.toString()}`,
          { headers: { Authorization: `Bearer ${token}` } },
        ),
        fetch(
          `${config.API_BASE_URL}/Hr/Payroll/IsLocked/${companyId}/${month}/${year}`,
          { headers: { Authorization: `Bearer ${token}` } },
        ),
      ]);
      if (!pRes.ok) throw new Error();
      setRows(await pRes.json());
      const lockData = await lRes.json();
      setIsLocked(!!lockData.isLocked);
    } catch (e) {
      toast('Failed to load payroll', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (companyId) loadRefs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, month, year, empFilter, departmentFilter]);

  const totals = useMemo(() => {
    return {
      count: rows.length,
      gross: rows.reduce((a, r) => a + Number(r.totalEarnings || 0), 0),
      deductions: rows.reduce((a, r) => a + Number(r.totalDeductions || 0), 0),
      net: rows.reduce((a, r) => a + Number(r.netSalary || 0), 0),
    };
  }, [rows]);

  const generate = async (employeeId = null) => {
    if (isLocked) {
      toast('This month is locked', 'warning');
      return;
    }
    setGenerating(true);
    try {
      const body = {
        CompanyId: companyId,
        Month: month,
        Year: year,
        EmployeeId: employeeId,
        DepartmentId: !employeeId && departmentFilter ? Number(departmentFilter) : null,
        UserId: user?.id || null,
      };
      const res = await fetch(`${config.API_BASE_URL}/Hr/Payroll/Generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        toast(data.message || 'Generate failed', 'error');
        return;
      }
      toast(`Generated ${data.count} payroll row(s)`);
      load();
    } catch (e) {
      toast('Network error', 'error');
    } finally {
      setGenerating(false);
    }
  };

  const toggleLock = async () => {
    const body = {
      CompanyId: companyId,
      Month: month,
      Year: year,
      IsLocked: !isLocked,
      UserId: user?.id || null,
    };
    const res = await fetch(`${config.API_BASE_URL}/Hr/Payroll/Lock`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(body),
    });
    if (res.ok) {
      toast(!isLocked ? 'Month locked' : 'Month unlocked');
      load();
    } else toast('Failed', 'error');
  };

  const openDetail = (r) => {
    setDetail(r);
    setDetailOpen(true);
  };

  const openAdjust = (r) => {
    setAdjustForm({
      id: r.id,
      incentive: r.incentive,
      bonus: r.bonus,
      otherEarnings: r.otherEarnings,
      otherDeductions: r.otherDeductions,
    });
    setAdjustOpen(true);
  };

  const openPay = (r) => {
    setPayTarget(r);
    setPayForm({
      paymentDate: new Date().toISOString().slice(0, 10),
      amount: r.balancePayable || 0,    // default = full pending
      payMode: 'Cash',
      referenceNo: '',
      bankName: '',
      remarks: '',
    });
    setPayOpen(true);
  };

  const savePay = async () => {
    if (!payTarget) return;
    const amt = Number(payForm.amount) || 0;
    if (amt <= 0) {
      toast('Enter a positive amount', 'warning');
      return;
    }
    const body = {
      Id: null,
      CompanyId: companyId,
      PayrollId: payTarget.id,
      PaymentDate: payForm.paymentDate,
      Amount: amt,
      PayMode: payForm.payMode,
      ReferenceNo: payForm.referenceNo || null,
      BankName: payForm.bankName || null,
      Remarks: payForm.remarks || null,
      UserId: user?.id || null,
    };
    const res = await fetch(`${config.API_BASE_URL}/Hr/SalaryPayment`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(body),
    });
    const data = await res.json();
    if (!res.ok || !data.success) {
      toast(data.message || 'Payment failed', 'error');
      return;
    }
    toast('Payment recorded');
    setPayOpen(false);
    load();
  };

  const openPayslip = (r) => {
    // New tab so the user keeps the payroll list intact while printing.
    window.open(`/admin/Hr/Payslip/${r.id}`, '_blank');
  };

  const saveAdjust = async () => {
    const body = {
      Id: adjustForm.id,
      CompanyId: companyId,
      Incentive: Number(adjustForm.incentive) || 0,
      Bonus: Number(adjustForm.bonus) || 0,
      OtherEarnings: Number(adjustForm.otherEarnings) || 0,
      OtherDeductions: Number(adjustForm.otherDeductions) || 0,
      UserId: user?.id || null,
    };
    const res = await fetch(`${config.API_BASE_URL}/Hr/Payroll/Adjust`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(body),
    });
    const data = await res.json();
    if (!res.ok || !data.success) {
      toast(data.message || 'Failed', 'error');
      return;
    }
    toast('Adjusted');
    setAdjustOpen(false);
    load();
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#F1F5F9', minHeight: '100%' }}>
      <Stack
        direction='row'
        spacing={1.5}
        alignItems='center'
        sx={{ mb: 2, justifyContent: 'space-between' }}>
        <Stack direction='row' spacing={1.5} alignItems='center'>
          <Box
            sx={{
              width: 42,
              height: 42,
              borderRadius: 2,
              bgcolor: '#1A56DB',
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
            <ReceiptIcon />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 800, fontSize: '1.1rem' }}>
              Payroll Processing
            </Typography>
            <Typography sx={{ fontSize: '0.75rem', color: '#64748B' }}>
              HR ·{' '}
              {new Date(year, month - 1, 1).toLocaleString('en-IN', {
                month: 'long',
              })}{' '}
              {year} ·{' '}
              {isLocked ? (
                <Chip
                  size='small'
                  label='Locked'
                  sx={{
                    bgcolor: '#fee2e2',
                    color: '#991b1b',
                    fontWeight: 600,
                    height: 18,
                  }}
                />
              ) : (
                <Chip
                  size='small'
                  label='Open'
                  sx={{
                    bgcolor: '#dcfce7',
                    color: '#166534',
                    fontWeight: 600,
                    height: 18,
                  }}
                />
              )}
            </Typography>
          </Box>
        </Stack>
        <Stack direction='row' spacing={1}>
          <Button
            variant={isLocked ? 'outlined' : 'outlined'}
            startIcon={isLocked ? <LockOpenIcon /> : <LockIcon />}
            onClick={toggleLock}
            sx={{
              borderColor: isLocked ? '#16a34a' : '#dc2626',
              color: isLocked ? '#16a34a' : '#dc2626',
            }}>
            {isLocked ? 'Unlock' : 'Lock'} Month
          </Button>
          <Button
            variant='contained'
            startIcon={<PlayArrowIcon />}
            onClick={() => generate(null)}
            disabled={generating || isLocked}
            sx={{ bgcolor: '#1A56DB', '&:hover': { bgcolor: '#1646b0' } }}>
            {generating ? 'Generating…' : 'Generate Bulk'}
          </Button>
        </Stack>
      </Stack>

      {/* KPI cards */}
      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        {[
          ['Employees', totals.count, '#1A56DB'],
          ['Gross', totals.gross, '#16a34a'],
          ['Deductions', totals.deductions, '#dc2626'],
          ['Net payable', totals.net, '#8b5cf6'],
        ].map(([label, val, color], i) => (
          <Grid item xs={6} sm={3} key={label}>
            <Card sx={{ borderLeft: `4px solid ${color}`, p: 1.5 }}>
              <Typography sx={{ fontSize: '0.7rem', color: '#64748B' }}>
                {label}
              </Typography>
              <Typography sx={{ fontWeight: 800, fontSize: '1.1rem' }}>
                {i === 0
                  ? val
                  : '₹' + Number(val).toLocaleString('en-IN')}
              </Typography>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1.5}
        sx={{ mb: 2 }}>
        <TextField
          select
          size='small'
          label='Month'
          value={month}
          onChange={(e) => setMonth(e.target.value)}
          sx={{ bgcolor: '#fff', minWidth: 140 }}>
          {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
            <MenuItem key={m} value={m}>
              {new Date(2000, m - 1, 1).toLocaleString('en-IN', {
                month: 'long',
              })}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          type='number'
          size='small'
          label='Year'
          value={year}
          onChange={(e) => setYear(Number(e.target.value))}
          sx={{ bgcolor: '#fff', width: 110 }}
        />
        <TextField
          select
          size='small'
          label='Department'
          value={departmentFilter}
          onChange={(e) => setDepartmentFilter(e.target.value)}
          sx={{ bgcolor: '#fff', minWidth: 180 }}>
          <MenuItem value=''>All</MenuItem>
          {departments.map((d) => (
            <MenuItem key={d.id} value={d.id}>
              {d.departmentName}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size='small'
          label='Employee'
          value={empFilter}
          onChange={(e) => setEmpFilter(e.target.value)}
          sx={{ bgcolor: '#fff', minWidth: 220 }}>
          <MenuItem value=''>All</MenuItem>
          {employees.map((e) => (
            <MenuItem key={e.id} value={e.id}>
              {e.employeeName} ({e.employeeCode})
            </MenuItem>
          ))}
        </TextField>
      </Stack>

      <Card sx={{ borderRadius: 2 }}>
        <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <TableContainer>
              <Table size='small'>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#F8FAFC' }}>
                    <TableCell sx={{ fontWeight: 700 }}>Employee</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Dept.</TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align='right'>
                      Days
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align='right'>
                      Basic
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align='right'>
                      OT
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align='right'>
                      Gross
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align='right'>
                      Deductions
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align='right'>
                      Net
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align='right'>
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={10} align='center' sx={{ py: 4 }}>
                        <Typography sx={{ color: '#94a3b8' }}>
                          No payroll generated. Click "Generate Bulk" or
                          generate per employee.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                  {rows.map((r) => {
                    const sc = STATUS_COLOR[r.status] || STATUS_COLOR.Draft;
                    return (
                      <TableRow key={r.id} hover>
                        <TableCell sx={{ fontWeight: 600 }}>
                          {r.employeeName}
                          <Typography sx={{ fontSize: '0.65rem', color: '#94a3b8' }}>
                            {r.employeeCode}
                          </Typography>
                        </TableCell>
                        <TableCell>{r.departmentName || '—'}</TableCell>
                        <TableCell align='right'>
                          P:{r.presentDays} L:{r.leaveDays} A:{r.absentDays}
                        </TableCell>
                        <TableCell align='right'>
                          ₹{Number(r.basicSalary).toLocaleString('en-IN')}
                        </TableCell>
                        <TableCell align='right'>
                          ₹{Number(r.overtimeAmount).toLocaleString('en-IN')}
                        </TableCell>
                        <TableCell align='right' sx={{ color: '#16a34a', fontWeight: 600 }}>
                          ₹{Number(r.totalEarnings).toLocaleString('en-IN')}
                        </TableCell>
                        <TableCell align='right' sx={{ color: '#dc2626' }}>
                          ₹{Number(r.totalDeductions).toLocaleString('en-IN')}
                        </TableCell>
                        <TableCell align='right' sx={{ fontWeight: 800 }}>
                          ₹{Number(r.netSalary).toLocaleString('en-IN')}
                        </TableCell>
                        <TableCell>
                          <Chip
                            size='small'
                            label={r.status}
                            sx={{
                              bgcolor: sc.bg,
                              color: sc.color,
                              fontWeight: 600,
                            }}
                          />
                        </TableCell>
                        <TableCell align='right'>
                          <Tooltip title='View'>
                            <IconButton size='small' onClick={() => openDetail(r)}>
                              <VisibilityIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title='Open payslip (print / save PDF)'>
                            <IconButton
                              size='small'
                              onClick={() => openPayslip(r)}
                              sx={{ color: '#475569' }}>
                              <PictureAsPdfIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                          {!isLocked && r.status !== 'Paid' && r.balancePayable > 0 && (
                            <Tooltip title='Record payment'>
                              <IconButton
                                size='small'
                                onClick={() => openPay(r)}
                                sx={{ color: '#16a34a' }}>
                                <PaymentIcon sx={{ fontSize: 18 }} />
                              </IconButton>
                            </Tooltip>
                          )}
                          {!isLocked && r.status !== 'Paid' && (
                            <>
                              <Tooltip title='Adjust manual fields'>
                                <IconButton
                                  size='small'
                                  onClick={() => openAdjust(r)}>
                                  <EditIcon sx={{ fontSize: 18 }} />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title='Recalculate'>
                                <IconButton
                                  size='small'
                                  onClick={() => generate(r.employeeId)}
                                  sx={{ color: '#1A56DB' }}>
                                  <RefreshIcon sx={{ fontSize: 18 }} />
                                </IconButton>
                              </Tooltip>
                            </>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Detail dialog */}
      <Dialog
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        maxWidth='md'
        fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>
          Payroll Detail
          {detail && (
            <Typography sx={{ fontSize: '0.85rem', color: '#64748B' }}>
              {detail.employeeName} ({detail.employeeCode}) ·{' '}
              {new Date(detail.year, detail.month - 1, 1).toLocaleString('en-IN', {
                month: 'long',
              })}{' '}
              {detail.year}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent dividers>
          {detail && (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography sx={{ fontWeight: 700, color: '#16a34a' }}>
                  Earnings
                </Typography>
                {[
                  ['Basic', detail.basicSalary],
                  ['HRA', detail.hra],
                  ['Conveyance', detail.conveyance],
                  ['Food Allowance', detail.foodAllowance],
                  ['Overtime', detail.overtimeAmount],
                  ['Incentive', detail.incentive],
                  ['Bonus', detail.bonus],
                  ['Service Share', detail.serviceShare],
                  ['Other Earnings', detail.otherEarnings],
                ].map(([k, v]) => (
                  <Stack
                    key={k}
                    direction='row'
                    justifyContent='space-between'
                    sx={{ py: 0.4 }}>
                    <Typography sx={{ fontSize: '0.85rem' }}>{k}</Typography>
                    <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>
                      ₹{Number(v).toLocaleString('en-IN')}
                    </Typography>
                  </Stack>
                ))}
                <Stack
                  direction='row'
                  justifyContent='space-between'
                  sx={{ pt: 1, borderTop: '1px solid #e5e7eb' }}>
                  <Typography sx={{ fontWeight: 700 }}>
                    Total Earnings
                  </Typography>
                  <Typography sx={{ fontWeight: 700, color: '#16a34a' }}>
                    ₹{Number(detail.totalEarnings).toLocaleString('en-IN')}
                  </Typography>
                </Stack>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography sx={{ fontWeight: 700, color: '#dc2626' }}>
                  Deductions
                </Typography>
                {[
                  ['Advance Recovery', detail.advanceRecovery],
                  ['Loan EMI', detail.loanEmi],
                  ['PF', detail.pf],
                  ['ESI', detail.esi],
                  ['TDS', detail.tds],
                  ['Professional Tax', detail.pt],
                  ['Other Deductions', detail.otherDeductions],
                ].map(([k, v]) => (
                  <Stack
                    key={k}
                    direction='row'
                    justifyContent='space-between'
                    sx={{ py: 0.4 }}>
                    <Typography sx={{ fontSize: '0.85rem' }}>{k}</Typography>
                    <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>
                      ₹{Number(v).toLocaleString('en-IN')}
                    </Typography>
                  </Stack>
                ))}
                <Stack
                  direction='row'
                  justifyContent='space-between'
                  sx={{ pt: 1, borderTop: '1px solid #e5e7eb' }}>
                  <Typography sx={{ fontWeight: 700 }}>
                    Total Deductions
                  </Typography>
                  <Typography sx={{ fontWeight: 700, color: '#dc2626' }}>
                    ₹{Number(detail.totalDeductions).toLocaleString('en-IN')}
                  </Typography>
                </Stack>
              </Grid>
              <Grid item xs={12}>
                <Box
                  sx={{
                    bgcolor: '#1A56DB',
                    color: '#fff',
                    p: 2,
                    borderRadius: 2,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mt: 1,
                  }}>
                  <Typography sx={{ fontWeight: 800, fontSize: '1.1rem' }}>
                    Net Salary
                  </Typography>
                  <Typography sx={{ fontWeight: 800, fontSize: '1.4rem' }}>
                    ₹{Number(detail.netSalary).toLocaleString('en-IN')}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Typography sx={{ fontSize: '0.75rem', color: '#64748B' }}>
                  Attendance snapshot: Present {detail.presentDays} · Half-day{' '}
                  {detail.absentDays > 0 ? '' : ''}· Leave {detail.leaveDays} ·
                  Absent {detail.absentDays} · OT {detail.otHours}h
                </Typography>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Payment dialog */}
      <Dialog
        open={payOpen}
        onClose={() => setPayOpen(false)}
        maxWidth='sm'
        fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>
          Record Salary Payment
          {payTarget && (
            <Typography sx={{ fontSize: '0.85rem', color: '#64748B' }}>
              {payTarget.employeeName} ({payTarget.employeeCode}) · Net ₹
              {Number(payTarget.netSalary).toLocaleString('en-IN')} · Pending ₹
              {Number(payTarget.balancePayable).toLocaleString('en-IN')}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ pt: 1 }}>
            <Grid item xs={6}>
              <TextField
                type='date'
                size='small'
                fullWidth
                label='Payment Date *'
                InputLabelProps={{ shrink: true }}
                value={payForm.paymentDate}
                onChange={(e) =>
                  setPayForm({ ...payForm, paymentDate: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                type='number'
                size='small'
                fullWidth
                label='Amount *'
                inputProps={{ min: 0 }}
                value={payForm.amount}
                onChange={(e) =>
                  setPayForm({ ...payForm, amount: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                size='small'
                fullWidth
                label='Mode *'
                value={payForm.payMode}
                onChange={(e) =>
                  setPayForm({ ...payForm, payMode: e.target.value })
                }>
                {['Cash', 'Bank', 'UPI', 'Cheque'].map((m) => (
                  <MenuItem key={m} value={m}>
                    {m}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                size='small'
                fullWidth
                label='Reference No.'
                value={payForm.referenceNo}
                onChange={(e) =>
                  setPayForm({ ...payForm, referenceNo: e.target.value })
                }
                helperText={
                  payForm.payMode === 'UPI'
                    ? 'UPI txn ID'
                    : payForm.payMode === 'Bank'
                      ? 'NEFT/IMPS ref'
                      : payForm.payMode === 'Cheque'
                        ? 'Cheque number'
                        : ' '
                }
              />
            </Grid>
            {(payForm.payMode === 'Bank' || payForm.payMode === 'Cheque') && (
              <Grid item xs={12}>
                <TextField
                  size='small'
                  fullWidth
                  label='Bank Name'
                  value={payForm.bankName}
                  onChange={(e) =>
                    setPayForm({ ...payForm, bankName: e.target.value })
                  }
                />
              </Grid>
            )}
            <Grid item xs={12}>
              <TextField
                size='small'
                fullWidth
                multiline
                rows={2}
                label='Remarks'
                value={payForm.remarks}
                onChange={(e) =>
                  setPayForm({ ...payForm, remarks: e.target.value })
                }
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setPayOpen(false)}>Cancel</Button>
          <Button
            variant='contained'
            onClick={savePay}
            sx={{ bgcolor: '#16a34a', '&:hover': { bgcolor: '#15803d' } }}>
            Record Payment
          </Button>
        </DialogActions>
      </Dialog>

      {/* Adjust dialog */}
      <Dialog
        open={adjustOpen}
        onClose={() => setAdjustOpen(false)}
        maxWidth='sm'
        fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Manual Adjustments</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ pt: 1 }}>
            <Grid item xs={6}>
              <TextField
                type='number'
                size='small'
                fullWidth
                label='Incentive'
                value={adjustForm.incentive}
                onChange={(e) =>
                  setAdjustForm({ ...adjustForm, incentive: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                type='number'
                size='small'
                fullWidth
                label='Bonus'
                value={adjustForm.bonus}
                onChange={(e) =>
                  setAdjustForm({ ...adjustForm, bonus: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                type='number'
                size='small'
                fullWidth
                label='Other Earnings'
                value={adjustForm.otherEarnings}
                onChange={(e) =>
                  setAdjustForm({
                    ...adjustForm,
                    otherEarnings: e.target.value,
                  })
                }
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                type='number'
                size='small'
                fullWidth
                label='Other Deductions'
                value={adjustForm.otherDeductions}
                onChange={(e) =>
                  setAdjustForm({
                    ...adjustForm,
                    otherDeductions: e.target.value,
                  })
                }
              />
            </Grid>
            <Grid item xs={12}>
              <Typography sx={{ fontSize: '0.75rem', color: '#64748B' }}>
                Note: Re-generating the payroll for this employee will keep
                these manual fields intact.
              </Typography>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setAdjustOpen(false)}>Cancel</Button>
          <Button variant='contained' onClick={saveAdjust}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={3500}
        onClose={() => setSnack({ ...snack, open: false })}>
        <Alert severity={snack.sev} variant='filled'>
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
